import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { LoadingSpinnerService, PromptService, ToastMessageService, errorToastValue } from '@k12/app-services';
import { ProgramTypes, ToastMessageType, toProgramTypeKey } from '@k12/models';

/**
 * `retry` means the enrollment period closed between the probe and the create (422), so the
 * caller should re-probe availability rather than treat this as a terminal failure.
 */
export type EnrollmentApplyOutcome = 'navigated' | 'retry' | 'failed';

/**
 * The single implementation of "can this subject apply, and do the apply" shared by the OS detail
 * card, the ESA+ detail card, and the household profile's Add Student button.
 */
@Injectable({ providedIn: 'root' })
export class EnrollmentApplyService {
  private readonly promptService = inject(PromptService);
  private readonly router = inject(Router);
  private readonly spinnerService = inject(LoadingSpinnerService);
  private readonly toastMessageService = inject(ToastMessageService);

  /**
   * Cold per-call probe holding no state, so the three calling surfaces cannot interfere with each
   * other. Callers are responsible for cancelling superseded probes (switchMap) to avoid a stale
   * response overwriting a newer one.
   */
  isPeriodOpen(programType: ProgramTypes, academicYearId: number): Observable<boolean> {
    return this.promptService.getEnrollmentApplication(toProgramTypeKey(programType), academicYearId).pipe(
      map((probe) => probe.isOpen),
      catchError(() => of(false))
    );
  }

  /**
   * Creates the application on behalf of the subject and navigates into the wizard. `onBehalfOfPersonId`
   * is omitted for Add Student and the Household Application, whose submissions create the person.
   */
  apply(
    programType: ProgramTypes,
    academicYearId: number,
    onBehalfOfPersonId?: string,
    studentId?: string
  ): Observable<EnrollmentApplyOutcome> {
    this.spinnerService.showSpinner();
    return this.promptService
      .createEnrollmentApplication(toProgramTypeKey(programType), academicYearId, onBehalfOfPersonId)
      .pipe(
        tap((app) => {
          this.spinnerService.hideSpinner();
          this.router.navigate(['/enrollment'], {
            queryParams: {
              sessionId: app.sessionId,
              programType,
              programToken: app.enrollmentProgramToken,
              personId: onBehalfOfPersonId,
              studentId: studentId
            }
          });
        }),
        map((): EnrollmentApplyOutcome => 'navigated'),
        catchError((err: HttpErrorResponse) => {
          this.spinnerService.hideSpinner();
          this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(err));
          return of<EnrollmentApplyOutcome>(err.status === 422 ? 'retry' : 'failed');
        })
      );
  }

  /**
   * Task-initiated apply (OS/ESA+ renewals). The assigned task's link carries a program token rather
   * than a program type plus academic year, because ActionUri lives on the task type and is shared by
   * every assigned instance; the server derives the year and the subject student from the task.
   *
   * Navigates with the programType so the wizard renders under the same shape as every other entry
   * path.
   */
  applyFromTask(
    programToken: string,
    originTaskId: string,
    originSubTaskId: string,
    programType?: ProgramTypes
  ): Observable<EnrollmentApplyOutcome> {
    this.spinnerService.showSpinner();
    return this.promptService
      .createEnrollmentApplicationFromTask(programToken, originTaskId, originSubTaskId)
      .pipe(
        tap((app) => {
          this.spinnerService.hideSpinner();
          this.router.navigate(['/enrollment'], {
            queryParams: {
              sessionId: app.sessionId,
              programToken: app.enrollmentProgramToken,
              programType
            },
            replaceUrl: true
          });
        }),
        map((): EnrollmentApplyOutcome => 'navigated'),
        catchError((err: HttpErrorResponse) => {
          this.spinnerService.hideSpinner();
          this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(err));
          return of<EnrollmentApplyOutcome>(err.status === 422 ? 'retry' : 'failed');
        })
      );
  }
}
